import {
  Author,
  Category,
  Channel,
  DocumentPage,
  SelectedMedia,
  MobileBlockDetail,
  Package,
  PlaylistJson,
  Podcast,
  Source,
  Topic,
  User,
  WebsiteBlockDetail,
} from '.';
import { AudioNarration } from './payload-types';
import { TypesenseContent } from '@/lib/model/typesense-content';
import { TvShow } from './tvShow';

export type ContentResponse = {
  Contents: {
    docs: Content[];
  } & DocumentPage;
};

export type SearchContentResponse = {
  docs: TypesenseContent[];
} & DocumentPage;

export type SingleContentResponse = {
  Content: Content;
};

export type Content = {
  id: string;
  type: 'main' | 'article' | 'video' | 'audio';
  subType?:
    | (
        | 'news'
        | 'opinion'
        | 'feature'
        | 'magazine'
        | 'videoCard'
        | 'videoPodcast'
        | 'tvContent'
        | 'dailyVideo'
        | 'dailyNewsBriefEpisode'
        | 'podcastEpisode'
        | 'featureEpisode'
      )
    | null;
  title: string;
  description?: string | null;
  body?: ContentBody;
  thumbnailImage?: string | SelectedMedia | null;
  coverImage?: string | SelectedMedia | null;
  media?: string | SelectedMedia | null;
  coverMedia?: string | SelectedMedia | null;
  author?: (string | null) | Author;
  otherAuthors?: null | Author[];
  videoCardTitle?: string | null;
  videoCardDescription?: string | null;
  podcast?: (string | null) | Podcast;
  reviewer?: (string | User)[] | null;
  relatedContent?: (string | Content)[] | null;
  category?: (string | Category)[] | null;
  country?:
    | {
        countryItem?: string | null;
        id?: string | null;
      }[]
    | null;
  tags?: string[] | null;
  backgroundColor?: string | null;
  location?: string | null;
  persona?: string | null;
  ai_summary?: string | null;
  audioNarration?: AudioNarration;
  keywordTags?:
    | {
        tag?: string | null;
        id?: string | null;
      }[]
    | null;
  viewCount?: number | null;
  shareCount?: number | null;
  likeCount?: number | null;
  newsSources?: (string | Source)[] | null;
  lang?: string | null;
  seo?: {
    metaTitle?: string | null;
    metaDescription?: string | null;
    metaTags?: string[] | null;
  };
  slug?: string | null;
  tvShow?: TvShow | null;
  channel?: (string | null) | Channel;
  showAuthorBio?: boolean | null;
  showByLine?: boolean | null;
  isOriginal?: boolean | null;
  isFeatured?: boolean | null;
  originalChannel?: (string | null) | Channel;
  originalContent?: (string | null) | Content;
  translatedContents?: (string | Content)[] | null;
  translatedToChannels?: (string | Channel)[] | null;
  primaryTopics: string | Topic;
  topics?: (string | Topic)[] | null;
  secondaryTopics?: (string | Topic)[] | null;
  owner?: (string | null) | User;
  needNarration?: boolean | null;
  assignee?: (string | null) | User;
  translationStatus?:
    | ('inProgress' | 'original' | 'translated' | 'noTranslation')
    | null;
  status?:
    | (
        | 'draft'
        | 'pending'
        | 'published'
        | 'changed'
        | 'ready_for_editor_review'
        | 'ready_for_copy_edit'
        | 'ready_for_qa'
        | 'ready_for_publish'
        | 'on_hold'
        | 'unpublished'
        | 'qa'
        | 'canceled'
      )
    | null;
  scheduleTime?: string | null;
  publishedAt?: string | null;
  publishedBy?: (string | null) | User;
  lastUpdatedChannel?: (string | null) | Channel;
  lastUpdatedBy?: (string | null) | User;
  mobileBlockDetails?: (string | MobileBlockDetail)[] | null;
  websiteBlockDetails?: (string | null) | WebsiteBlockDetail;
  summaryDetails?: {
    title?: string | null;
    description?: string | null;
    body?: string | null;
    author?: (string | null) | Author;
    content?: (string | null) | Content;
  };
  duration?: string | null;
  content?: (string | null) | Content;
  readingTime?: {
    text?: string | null;
    minutes?: number | null;
    word?: number | null;
  };
  hasfollowStory?: boolean | null;
  followStory?: (string | null) | Content;
  package?: (string | null) | Package;
  updatedAt: string;
  createdAt: string;
  _status?: ('draft' | 'published') | null;
};

export type ContentBody = {
  type: BodyType;
  content: BodyContent[];
};

export type BodyContent = {
  type: BodyContentType;
  attrs: ContentAttrs;
  content?: ContentContent[];
};

export type ContentAttrs = {
  class?: null;
  textAlign?: TextAlign;
  dir?: Dir | null;
  src?: string;
  url?: string;
  width?: string;
  align?: string;
  poster?: string;
  playlistUrl?: string;
  playlistJson?: PlaylistJson;
  caption?: string;
  id?: string;
  'data-toc-id'?: string;
  level?: number;
};

export enum Dir {
  LTR = 'ltr',
}

export enum TextAlign {
  Left = 'left',
}

export type ContentContent = {
  type: PurpleType;
  text?: string;
  marks?: Mark[];
};

export type Mark = {
  type: MarkType;
  attrs?: MarkAttrs;
};

export type MarkAttrs = {
  href: string;
  target: string;
  rel: string;
  class: string;
};

export enum MarkType {
  Bold = 'bold',
  Italic = 'italic',
  Link = 'link',
}

export enum PurpleType {
  HardBreak = 'hardBreak',
  Text = 'text',
  Heading = 'heading',
}

export enum BodyContentType {
  Heading = 'heading',
  ImageBlock = 'imageBlock',
  Paragraph = 'paragraph',
  VideoBlock = 'videoBlock',
  AudioBlock = 'audioBlock',
  Twitter = 'twitter',
  Youtube = 'youtube',
}

export enum BodyType {
  Doc = 'doc',
}
